import React, { useState, useEffect } from "react";
import API from "@/api";
import { toast } from "sonner";
import { Bell, CheckCheck, HandHelping, CalendarDays, BellOff } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { isSupported as isNotifSupported, getPermission, requestPermission, showNotification } from "@/lib/browserNotifications";

const NOTIF_ICONS = {
    TASK_ASSIGNED: "📋",
    COMMENT_ADDED: "💬",
    TASK_DUE_SOON: "⏰",
    TASK_OVERDUE: "🔴",
    TASK_STANDBY: "⏸️",
    ASSISTANCE_REQUEST: "🤝",
    ASSISTANCE_RESPONSE: "✅",
    EVENT_INVITE: "📅",
    EVENT_RESPONSE: "📅",
};

const NOTIF_TYPE_LABELS = {
    TASK_ASSIGNED: "Task atribuit",
    COMMENT_ADDED: "Comentariu nou",
    TASK_DUE_SOON: "Scadent curând",
    TASK_OVERDUE: "Task întârziat",
    TASK_STANDBY: "Pus în Stand By",
    ASSISTANCE_REQUEST: "Cerere asistență",
    ASSISTANCE_RESPONSE: "Răspuns asistență",
    EVENT_INVITE: "Invitație eveniment",
    EVENT_RESPONSE: "Răspuns eveniment",
};

export default function NotificationsPage() {
    const [notifications, setNotifications] = useState([]);
    const [loading, setLoading] = useState(true);
    const navigate = useNavigate();

    const fetchNotifications = () => {
        API.get("/notifications")
            .then(r => setNotifications(r.data))
            .finally(() => setLoading(false));
    };

    useEffect(() => { fetchNotifications(); }, []);

    const markRead = async (id) => {
        await API.post(`/notifications/${id}/read`);
        setNotifications(prev => prev.map(n => n.id === id ? { ...n, is_read: true } : n));
    };

    const markAllRead = async () => {
        await API.post("/notifications/read-all");
        setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
        toast.success("Toate notificările marcate ca citite");
    };

    const openNotification = async (notif) => {
        if (!notif.is_read) markRead(notif.id);

        // Assistance notifications → pagina Asistență
        if (notif.entity_type === "assistance") {
            navigate("/assistance");
            return;
        }
        // Event invitations → Calendar
        if (notif.entity_type === "event") {
            navigate("/calendar");
            return;
        }
        // Task notifications → board cu task deschis
        if (notif.entity_type === "task" && notif.entity_id) {
            let boardId = notif.entity_meta?.board_id;
            if (!boardId) {
                try {
                    const r = await API.get(`/tasks/${notif.entity_id}/locate`);
                    boardId = r.data.board_id;
                } catch {
                    toast.error("Taskul nu mai este disponibil");
                    return;
                }
            }
            if (boardId) {
                navigate(`/boards/${boardId}?task=${notif.entity_id}`);
            } else {
                toast.error("Nu am găsit board-ul acestui task");
            }
        }
    };

    const unreadCount = notifications.filter(n => !n.is_read).length;
    const [notifPerm, setNotifPerm] = useState(() => getPermission());

    const handleEnableBrowserNotif = async () => {
        const perm = await requestPermission();
        setNotifPerm(perm);
        if (perm === "granted") {
            toast.success("Notificările desktop sunt active");
            showNotification("TOM — test reușit!", { body: "Vei primi alerte chiar când tab-ul nu e activ.", force: true });
        } else if (perm === "denied") {
            toast.error("Browser-ul a blocat notificările. Activează-le din setările site-ului (lacăt în bara de adresă).");
        }
    };

    return (
        <div className="p-6 max-w-2xl mx-auto" style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}>
            <div className="flex items-center justify-between mb-6">
                <div>
                    <h1 className="text-2xl font-bold text-[#111827]" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>Notificări</h1>
                    {unreadCount > 0 && <p className="text-sm text-[#9CA3AF] mt-1">{unreadCount} necitite</p>}
                </div>
                {unreadCount > 0 && (
                    <button onClick={markAllRead} data-testid="mark-all-read-btn"
                        className="flex items-center gap-1.5 text-xs text-[#002FA7] border border-[#002FA7]/30 px-3 py-2 rounded-md hover:bg-blue-50 transition-colors">
                        <CheckCheck size={14} /> Marchează toate citite
                    </button>
                )}
            </div>

            {/* Browser desktop notifications status */}
            {isNotifSupported() && (
                <div data-testid="notif-perm-status" className="mb-5 flex items-center gap-3 p-3 border border-[#E4E4E7] rounded-md bg-white">
                    {notifPerm === "granted" ? (
                        <>
                            <Bell size={16} className="text-[#10B981]" />
                            <div className="flex-1 text-sm text-[#111827]">
                                <span className="font-semibold">Notificările desktop sunt active</span>
                                <span className="text-[#6B7280] block text-xs">Vei primi alerte instant când tab-ul TOM nu e activ.</span>
                            </div>
                            <button onClick={() => showNotification("Test notificare TOM", { body: "Așa va arăta o alertă reală.", force: true })}
                                data-testid="test-notif-btn"
                                className="text-xs text-[#002FA7] hover:underline">Trimite test</button>
                        </>
                    ) : notifPerm === "denied" ? (
                        <>
                            <BellOff size={16} className="text-red-500" />
                            <div className="flex-1 text-sm text-[#111827]">
                                <span className="font-semibold">Notificările sunt blocate de browser</span>
                                <span className="text-[#6B7280] block text-xs">Activează-le manual din setările site-ului (icon-ul de lacăt din bara de adresă → Notificări → Permite).</span>
                            </div>
                        </>
                    ) : (
                        <>
                            <Bell size={16} className="text-[#9CA3AF]" />
                            <div className="flex-1 text-sm text-[#111827]">
                                <span className="font-semibold">Activează alertele desktop</span>
                                <span className="text-[#6B7280] block text-xs">Primește notificări chiar când tab-ul TOM nu e activ.</span>
                            </div>
                            <button onClick={handleEnableBrowserNotif}
                                data-testid="enable-notif-btn"
                                className="text-xs font-semibold bg-[#002FA7] text-white px-3 py-1.5 rounded hover:bg-[#002482]">
                                Activează
                            </button>
                        </>
                    )}
                </div>
            )}

            {loading ? (
                <div className="space-y-2">
                    {[...Array(5)].map((_, i) => <div key={i} className="h-16 bg-white border border-[#E4E4E7] rounded-md animate-pulse" />)}
                </div>
            ) : notifications.length === 0 ? (
                <div className="text-center py-16" data-testid="empty-notifications">
                    <Bell size={40} className="text-[#E4E4E7] mx-auto mb-3" />
                    <p className="text-sm text-[#9CA3AF]">Nicio notificare</p>
                </div>
            ) : (
                <div className="space-y-1" data-testid="notifications-list">
                    {notifications.map(notif => {
                        const typeLabel = NOTIF_TYPE_LABELS[notif.type] || notif.type;
                        const emoji = NOTIF_ICONS[notif.type] || "🔔";
                        return (
                            <div key={notif.id}
                                data-testid={`notification-${notif.id}`}
                                className={`flex items-start gap-3 p-4 rounded-md border transition-colors cursor-pointer
                                    ${notif.is_read ? "bg-white border-[#E4E4E7] opacity-70" : "bg-blue-50/50 border-[#002FA7]/20"}`}
                                onClick={() => openNotification(notif)}
                            >
                                <span className="text-xl flex-shrink-0" aria-hidden>{emoji}</span>
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center justify-between gap-2 mb-0.5">
                                        <span className="text-xs font-bold text-[#9CA3AF] uppercase tracking-wide">
                                            {typeLabel}
                                        </span>
                                        <span className="text-xs text-[#9CA3AF] flex-shrink-0">
                                            {new Date(notif.created_at).toLocaleString("ro-RO", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}
                                        </span>
                                    </div>
                                    <p className="text-sm font-semibold text-[#111827]">{notif.title}</p>
                                    {notif.body && <p className="text-xs text-[#4B5563] mt-0.5 line-clamp-2">{notif.body}</p>}
                                </div>
                                {!notif.is_read && <div className="w-2 h-2 rounded-full bg-[#002FA7] mt-2 flex-shrink-0" />}
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
}
